import { toast } from 'react-toastify';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const CsiProgrammesInputPage = () => {
    // Get current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // State variables to manage form data and loading state
    const [formData, setFormData] = useState({
        educationalSupport: 0,
        communityUpliftment: 0,
        sportForYouth: 0,
        primaryHealthcare: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [programData, setProgramData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth + 1);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [hasPeriodData, setHasPeriodData] = useState(false);
    const [originalFormData, setOriginalFormData] = useState(null);
    const [pendingInput, setPendingInput] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    
    // Use this flag to prevent multiple modals during a single edit
    const editingField = useRef(false);
    const dataInitialized = useRef(false);

    const { apiFetch, logout } = useAuth();
    const navigate = useNavigate();

    const months = [
        { value: 1, name: "January" },
        { value: 2, name: "February" },
        { value: 3, name: "March" },
        { value: 4, name: "April" },
        { value: 5, name: "May" },
        { value: 6, name: "June" },
        { value: 7, name: "July" },
        { value: 8, name: "August" },
        { value: 9, name: "September" },
        { value: 10, name: "October" },
        { value: 11, name: "November" },
        { value: 12, name: "December" }
    ];

    // Generate years dynamically from 2024 to the current year (newest first)
    const years = Array.from(
        { length: currentYear - 2024 + 1 },
        (_, i) => currentYear - i
    );

    // Function to determine if a month should be disabled (future months)
    const isMonthDisabled = (monthValue) => {
        if (selectedYear > currentYear) {
            return false;
        }
        if (selectedYear === currentYear && monthValue > currentMonth + 1) {
            return true;
        }
        return false;
    };

    // Calculate selected period in YYYYMM format
    const getSelectedPeriod = () => {
        return `${selectedYear}${selectedMonth.toString().padStart(2, '0')}`;
    };

    // Handle month change
    const handleMonthChange = (e) => {
        const newMonth = parseInt(e.target.value, 10);
        setSelectedMonth(newMonth);

        // If we were in the middle of editing, cancel that edit
        if (pendingInput) {
            setPendingInput(null);
        }
        editingField.current = false;

        loadDataForPeriod(`${selectedYear}${newMonth.toString().padStart(2, '0')}`);
    };

    // Handle year change
    const handleYearChange = (e) => {
        const newYear = parseInt(e.target.value, 10);
        setSelectedYear(newYear);

        // If we were in the middle of editing, cancel that edit
        if (pendingInput) {
            setPendingInput(null);
        }
        editingField.current = false;

        // Update month if needed
        let updatedMonth = selectedMonth;
        if (newYear === currentYear && selectedMonth > currentMonth + 1) {
            updatedMonth = currentMonth + 1;
            setSelectedMonth(updatedMonth);
        }

        loadDataForPeriod(`${newYear}${updatedMonth.toString().padStart(2, '0')}`);
    };

    // Handle input focus - mark that we're starting an edit
    const handleInputFocus = (e) => {
        const { name } = e.target;
        // Store the starting value when we focus
        editingField.current = name;
    };

    // Handle input blur - process the edit when focus leaves the field
    const handleInputBlur = () => {
        // If we have a pending edit and we're not showing the modal yet
        if (pendingInput && !showConfirmModal && hasPeriodData) {
            setShowConfirmModal(true);
        }

        // Reset the editing state
        editingField.current = false;
    };

    // Confirm form data change
    const confirmChange = () => {
        if (pendingInput) {
            // Keep the form data we're already showing
            setPendingInput(null);
        }
        setShowConfirmModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Only allow numbers
        if (value === '' || /^\d*$/.test(value)) {
            const intValue = parseInt(value, 10) || 0;

            // Create the updated form state
            const newFormData = {
                ...formData,
                [name]: intValue
            };

            // If we're editing a field with existing data, queue up the change
            // BUT also show the changed value to the user immediately
            if (hasPeriodData) {
                setPendingInput({ formData: newFormData, fieldName: name });

                // Show the input change immediately for better UX
                setFormData(newFormData);

            } else {
                // If there's no existing data, apply change immediately
                setFormData(newFormData);
            }
        }
    };

    // Cancel form data change
    const cancelChange = () => {
        setPendingInput(null);
        setShowConfirmModal(false);

        // If we have original data, reset the form
        if (originalFormData) {
            setFormData(originalFormData);
        }
    };

    // API call to fetch existing data
    const fetchProgrammesData = async () => {
        try {
            const response = await apiFetch('/api/Marketing/csi-programmes');
            const data = await response.json();

            if (data.success && data.data) {
                setProgramData(data.data);

                // Load data for the current period after fetching all programs
                const currentPeriod = getSelectedPeriod();
                loadDataForPeriod(currentPeriod, data.data);

                // Mark that we've initialized the data
                dataInitialized.current = true;
            } else {
                toast.error("Failed to load CSI programme data");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("Failed to load CSI programme data");
        } 
    };

    // Load data for a specific period
    const loadDataForPeriod = (period, programDataSource = null) => {
        // Use passed data if available, otherwise use state
        const dataSource = programDataSource || programData;

        // If we don't have data yet, don't try to load
        if (!dataSource || !dataSource.length) return;

        // Find data for the selected period
        const periodData = dataSource.find(item => item.period === parseInt(period, 10));

        if (periodData) {
            // Data exists for this period
            setHasPeriodData(true);
            const newFormData = {
                educationalSupport: periodData.educationalSupport,
                communityUpliftment: periodData.communityUpliftment,
                sportForYouth: periodData.sportForYouth,
                primaryHealthcare: periodData.primaryHealthcare
            };

            setFormData(newFormData);
            setOriginalFormData(newFormData); // Store original data for reset
        } else {
            // No data for this period
            setHasPeriodData(false);
            setFormData({
                educationalSupport: 0,
                communityUpliftment: 0,
                sportForYouth: 0,
                primaryHealthcare: 0
            });
            setOriginalFormData(null);
        }
    };

    // API call to save data
    const handleSave = async (e) => {
        e.preventDefault();

        const csiProgramData = {
            educationalSupport: formData.educationalSupport,
            communityUpliftment: formData.communityUpliftment,
            sportForYouth: formData.sportForYouth,
            primaryHealthcare: formData.primaryHealthcare,
            period: parseInt(getSelectedPeriod(), 10)
        };

        setIsLoading(true);

        try {
            const response = await apiFetch('/api/Marketing/csi-programmes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(csiProgramData)
            });

            const data = await response.json();

            if (data.success) {
                toast.success("CSI programme data saved successfully");

                // Update our local program data to reflect the save
                const updatedProgramData = [...programData];
                const existingIndex = updatedProgramData.findIndex(p => p.period === csiProgramData.period);

                if (existingIndex >= 0) {
                    // Update existing record
                    updatedProgramData[existingIndex] = {
                        ...updatedProgramData[existingIndex],
                        ...csiProgramData
                    };
                } else {
                    // Add new record
                    updatedProgramData.push({
                        ...csiProgramData,
                        createdDate: new Date().toISOString(),
                        modifiedDate: new Date().toISOString()
                    });
                }

                // Update program data state
                setProgramData(updatedProgramData);

                // Update form state to reflect saved state
                setFormData(csiProgramData);
                setOriginalFormData(csiProgramData);
                setHasPeriodData(true);
            } else {
                toast.error("Failed to save CSI programme data");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while saving data");
        } finally {
            setIsLoading(false);
        }
    };

    // Load initial data only once on component mount
    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => {
        const loadInitial = async () => {
            await fetchProgrammesData();
        };
        loadInitial();
    }, []);

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div>
            <div className="csi-container">
                <h1>CSI Programme Beneficiaries</h1>

                {/* Month and Year Selection */}
                <div className="period-selector">
                    <h3>Select Period</h3>
                    <div className="month-year-selector">
                        <div className="selector-container">
                            <label htmlFor="month-select">Month:</label>
                            <select
                                id="month-select"
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                className="form-control"
                            >
                                {months.map(month => (
                                    <option
                                        key={month.value}
                                        value={month.value}
                                        disabled={isMonthDisabled(month.value)}
                                        className={isMonthDisabled(month.value) ? 'disabled-option' : ''}
                                    >
                                        {month.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="selector-container">
                            <label htmlFor="year-select">Year:</label>
                            <select
                                id="year-select"
                                value={selectedYear}
                                onChange={handleYearChange}
                                className="form-control"
                            >
                                {years.map(year => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <p>Selected Period: {getSelectedPeriod()}</p>
                    {hasPeriodData && (
                        <p className="data-exists">Data exists for this period</p>
                    )}
                </div>

                <div className="save-button-container">
                    <button
                        className="save-button"
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Save CSI Data'}
                    </button>
                </div>

                <div className="csi-programmes">
                    <div className="programme-row">
                        <label htmlFor="educationalSupport">Educational Support</label>
                        <input
                            type="text"
                            id="educationalSupport"
                            name="educationalSupport"
                            value={formData.educationalSupport}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Enter amount"
                            inputMode="numeric"
                        />
                    </div>

                    <div className="programme-row">
                        <label htmlFor="communityUpliftment">Community Upliftment</label>
                        <input
                            type="text"
                            id="communityUpliftment"
                            name="communityUpliftment"
                            value={formData.communityUpliftment}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Enter amount"
                            inputMode="numeric"
                        />
                    </div>

                    <div className="programme-row">
                        <label htmlFor="sportForYouth">Sport for Youth</label>
                        <input
                            type="text"
                            id="sportForYouth"
                            name="sportForYouth"
                            value={formData.sportForYouth}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Enter amount"
                            inputMode="numeric"
                        />
                    </div>

                    <div className="programme-row">
                        <label htmlFor="primaryHealthcare">Primary Healthcare</label>
                        <input
                            type="text"
                            id="primaryHealthcare"
                            name="primaryHealthcare"
                            value={formData.primaryHealthcare}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Enter amount"
                            inputMode="numeric"
                        />
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>Confirm Changes</h3>
                        </div>
                        <div className="modal-body">
                            <p>Data already exists for this period. Are you sure you want to modify it?</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={cancelChange}>No</button>
                            <button className="btn btn-primary" onClick={confirmChange}>Yes</button>
                        </div>
                    </div>
                </div>
            )}
</div>
    );
};

export default CsiProgrammesInputPage;